import SearchIcon from '@mui/icons-material/Search'
import CircularProgress from '@mui/material/CircularProgress'

export type SearchMode = 'prompt' | 'venue-name'

interface Props {
  value: string
  onChange: (value: string) => void
  onSearch: () => void
  loading: boolean
  searchMode: SearchMode
  onSearchModeChange: (mode: SearchMode) => void
}

const VenueSearchInput = ({
  value,
  onChange,
  onSearch,
  loading,
  searchMode,
  onSearchModeChange,
}: Props) => (
  <div className='flex flex-col gap-4'>
    <span className='font-medium text-sm text-black tracking-[0.07px]'>Refine your results:</span>
    <div className='flex flex-wrap items-center gap-3'>
      {(['prompt', 'venue-name'] as const).map((mode) => (
        <label key={mode} className='cursor-pointer'>
          <input
            type='radio'
            name='search-mode'
            value={mode}
            checked={searchMode === mode}
            onChange={() => onSearchModeChange(mode)}
            className='sr-only'
          />
          <div className='flex items-center gap-1.5'>
            <div
              className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${
                searchMode === mode ? 'border-blue-600' : 'border-[#bfbfbf]'
              }`}>
              {searchMode === mode && (
                <div className='w-[10px] h-[10px] rounded-full bg-[#1270B8]' />
              )}
            </div>
            <span className='text-sm font-medium text-black tracking-[0.07px]'>
              {mode === 'prompt' ? 'Search by prompt' : 'Search by venue name'}
            </span>
          </div>
        </label>
      ))}
    </div>
    <div className='border border-[#bfbfbf] rounded-[8px] h-[105px] p-[8px] flex gap-[10px] items-center'>
      <textarea
        className={`flex-1 h-full pl-[8px] pr-[26px] pt-[8px] text-base text-black resize-none outline-none
          bg-transparent placeholder:text-[#bfbfbf]`}
        placeholder={
          searchMode === 'prompt'
            ? 'e.g. I’m looking for a meeting room with natural light and air conditioning.'
            : 'Venue name: Type in the venues name -  if you don’t see what you’re looking for in the ' +
              'inventory, don’t worry - our experts can still source it for you.'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            onSearch()
          }
        }}
      />
      <button
        type='button'
        onClick={onSearch}
        disabled={loading}
        className={`bg-[#af231c] rounded-full !p-2 shrink-0 cursor-pointer hover:bg-[#8b1a15] transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center w-8 h-8`}
        aria-label='Search venues'>
        {loading ? (
          <CircularProgress size={16} sx={{ color: 'white' }} />
        ) : (
          <SearchIcon width={16} height={16} className='text-white' />
        )}
      </button>
    </div>
  </div>
)

export default VenueSearchInput
